import { NavLink } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import {
  avatarBg,
  avatarBorder,
  avatarText,
  chevronColor,
  drawerDivider,
  drawerItemHoverBg as itemHoverBg,
  drawerItemText as itemText,
  groupTitleText,
  itemButtonHoverBg,
  itemButtonHoverText,
  itemButtonText,
  itemHoverText,
  panelBg as drawerBg,
  panelBorder as drawerBorder,
  sectionHeaderHoverBg,
  sectionHeaderText,
  subGroupBg,
  footerText,
} from '../../NavLayout/styled/palette';

// sectionHeaderText active and footer hover share the same light/dark pair
// as the general itemHoverText helper.
const sectionHeaderTextActive = itemHoverText;
const footerHoverText = itemHoverText;

export const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${(props) => props.backgroundColor || '#000'}!important;
  }
`;

export const MobileContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export const TopBar = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: ${(props) => props.theme.colors.primary};
  height: 60px;
  width: 100%;
  z-index: 9;
  top: 0;
`;

export const TopBarLogo = styled.img`
  height: 30px;
  width: auto;
`;

export const TopBarLogoWrap = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 8px;
`;

export const HamburgerButton = styled.div`
  width: 20px;
  height: 20px;
  position: absolute;
  margin: auto;
  top: 0;
  left: 20px;
  bottom: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 200;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  transition: opacity 250ms ease-in-out;
`;

export const Drawer = styled.div`
  width: 280px;
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background: ${drawerBg};
  position: fixed;
  transform: ${(props) =>
    props.isVisible ? 'translateX(0%)' : 'translateX(-100%)'};
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 9999;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const DrawerHeader = styled.div`
  padding: 16px;
  background: ${(props) => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

export const DrawerHeaderLogo = styled.img`
  height: 30px;
  width: auto;
`;

export const DrawerCloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  transition: background 150ms ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

export const DrawerAccountRow = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${drawerBorder};
`;

export const DrawerSection = styled.div``;

export const DrawerSectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-family: ${(props) => props.theme.fonts.primary};
  font-weight: 600;
  font-size: 18px;
  color: ${(props) =>
    props.isActive ? sectionHeaderTextActive(props) : sectionHeaderText(props)};
  background: ${(props) =>
    props.isActive ? props.theme.colors.primaryOpacity(0.12) : 'transparent'};
  cursor: pointer;
  gap: 12px;
  user-select: none;
  transition: background 150ms ease;

  &:hover {
    background: ${sectionHeaderHoverBg};
  }
`;

export const DrawerExpandChevron = styled.svg`
  margin-left: auto;
  width: 18px;
  height: 18px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  color: ${chevronColor};
  transform: ${(props) => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 200ms ease-in-out;
  flex-shrink: 0;
`;

export const DrawerSubGroup = styled.div`
  max-height: ${(props) => (props.isExpanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 300ms ease-in-out;
  background: ${subGroupBg};
`;

export const DrawerItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 10px 16px 10px 48px;
  font-family: ${(props) => props.theme.fonts.primary};
  font-weight: 500;
  font-size: 15px;
  color: ${itemText};
  text-decoration: none;
  gap: 8px;
  transition: all 150ms ease;

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }

  &.active {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 700;
    background: ${(props) => props.theme.colors.primaryOpacity(0.08)};
  }
`;

export const DrawerItemExternal = styled.a`
  display: flex;
  align-items: center;
  padding: 10px 16px 10px 48px;
  font-family: ${(props) => props.theme.fonts.primary};
  font-weight: 500;
  font-size: 15px;
  color: ${itemText};
  text-decoration: none;
  gap: 8px;
  transition: all 150ms ease;

  &:hover {
    color: ${itemHoverText};
    background: ${itemHoverBg};
  }
`;

export const DrawerItemButton = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px 10px 48px;
  font-family: ${(props) => props.theme.fonts.primary};
  font-weight: 500;
  font-size: 15px;
  color: ${itemButtonText};
  cursor: pointer;
  gap: 8px;
  transition: all 150ms ease;

  &:hover {
    color: ${itemButtonHoverText};
    background: ${itemButtonHoverBg};
  }
`;

export const MobileIcon = styled.svg`
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex-shrink: 0;
`;

export const DrawerDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.5;
  flex-shrink: 0;
`;

export const DrawerExpandGroupTitle = styled.div`
  padding: 8px 16px 4px 48px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${groupTitleText};
`;

export const DrawerDivider = styled.div`
  height: 1px;
  background: ${drawerDivider};
  margin: 8px 0;
`;

export const DrawerFooter = styled.div`
  padding: 4px 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const DrawerFooterItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  font-family: ${(props) => props.theme.fonts.primary};
  font-weight: 500;
  font-size: 16px;
  color: ${footerText};
  cursor: pointer;
  gap: 12px;

  &:hover {
    color: ${footerHoverText};
  }
`;

export const DrawerAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${avatarBg};
  border: 2px solid ${avatarBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: ${avatarText};
  flex-shrink: 0;
`;

export const ContentArea = styled.div`
  padding-top: 60px;
  width: 100%;
`;
